// client/src/components/cursos/MatriculasPage.js
// Alunos matriculados num curso — paridade com
// mobile/app/cursos/[id]/matriculados.tsx (mobile já tinha isso, client
// não tinha nenhuma UI de matrícula até agora). Matrícula em si passou a
// ser automática no pagamento confirmado (ver Venda.js post('save')) — esta
// tela fica pra acompanhar progresso, concluir manualmente em casos
// excepcionais e cancelar.
import React, { Fragment, useState } from 'react';
import { Loader2, Users, XCircle, CheckCircle, Ban, MoreVertical } from 'lucide-react';
import { useMatriculasCurso, useMutateMatriculasCurso } from '../../hooks/useMatriculasCurso';
import PageHeader from '../layout/PageHeader';
import ActionsDrawer from '../common/ActionsDrawer';

const STATUS_BADGE_CLASS = {
    Matriculado: 'badge-info',
    Concluido: 'badge-success',
    Cancelado: 'badge-danger',
};

const formatarData = (data) => data ? new Date(data).toLocaleDateString('pt-BR') : '—';

const MatriculasPage = ({ curso, onClose }) => {
    const { data: matriculas, isLoading } = useMatriculasCurso({ curso: curso?.id });
    const { concluirMatricula, cancelarMatricula, isPending } = useMutateMatriculasCurso();
    const [selected, setSelected] = useState(null);

    const handleConcluir = (matricula) => {
        if (window.confirm('Marcar esta matrícula como concluída? Isso libera a emissão do certificado.')) {
            concluirMatricula.mutate(matricula._id, { onSuccess: () => setSelected(null) });
        }
    };

    const handleCancelar = (matricula) => {
        if (window.confirm('Tem certeza que deseja cancelar esta matrícula?')) {
            cancelarMatricula.mutate(matricula._id, { onSuccess: () => setSelected(null) });
        }
    };

    const lista = matriculas || [];

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Users />}
                    title={`Matriculados — ${curso?.nome || ''}`}
                    subtitle={`${lista.length} aluno(s)`}
                    actions={
                        <button onClick={onClose} className="action-button action-button-secondary">
                            <XCircle size={18} className="icon" /> Fechar
                        </button>
                    }
                />
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin" />
                            <span>Carregando matrículas...</span>
                        </div>
                    ) : lista.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th>Aluno</th>
                                    <th>Status</th>
                                    <th>Progresso</th>
                                    <th>Matriculado em</th>
                                    <th>Concluído em</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lista.map((matricula) => {
                                    const clienteNome = typeof matricula.cliente === 'object' ? matricula.cliente?.user?.name : '—';
                                    return (
                                        <tr key={matricula._id}>
                                            <td>{clienteNome || 'Cliente'}</td>
                                            <td>
                                                <span className={`badge ${STATUS_BADGE_CLASS[matricula.status] || ''}`}>
                                                    {matricula.status}
                                                </span>
                                            </td>
                                            <td>{matricula.progresso?.percentualConcluido ?? 0}%</td>
                                            <td>{formatarData(matricula.dataMatricula)}</td>
                                            <td>{formatarData(matricula.dataConclusao)}</td>
                                            <td className="text-right">
                                                {/* Era um "⋯" literal em vez do icone MoreVertical
                                                    usado em todo o resto do app pro mesmo menu */}
                                                <button
                                                    className="action-button-icon"
                                                    onClick={() => setSelected(matricula)}
                                                    title="Ações"
                                                >
                                                    <MoreVertical size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <Users size={48} />
                            <h3>Nenhum aluno matriculado</h3>
                            <p>A matrícula acontece automaticamente quando o pagamento do curso é confirmado.</p>
                        </div>
                    )}
                </div>
            </div>

            <ActionsDrawer
                open={!!selected}
                onClose={() => setSelected(null)}
                title={typeof selected?.cliente === 'object' ? selected.cliente?.user?.name || 'Aluno' : 'Aluno'}
                actions={selected ? [
                    ...(selected.status === 'Matriculado' ? [{
                        key: 'concluir',
                        label: 'Marcar como Concluído',
                        icon: <CheckCircle size={20} className="icon" />,
                        onClick: () => handleConcluir(selected),
                        loading: isPending,
                    }] : []),
                    ...(selected.status !== 'Cancelado' ? [{
                        key: 'cancelar',
                        label: 'Cancelar Matrícula',
                        icon: <Ban size={20} className="icon" />,
                        onClick: () => handleCancelar(selected),
                        destructive: true,
                        loading: isPending,
                    }] : []),
                ] : []}
            />
        </Fragment>
    );
};

export default MatriculasPage;
